// Chart.js wiring for chart blocks. Chart.js is loaded on first use in its own
// chunk, so customers who never ask for a graph never download it.

const SERIES_COLORS = ['#5d7afd', '#ffb74d', '#4caf85', '#f06a6a', '#9b7bf2', '#42b8d6'];

const NUMBER_FORMAT = new Intl.NumberFormat('he-IL', { maximumFractionDigits: 2 });

export const formatChartNumber = value => NUMBER_FORMAT.format(value);

let chartPromise = null;

// Resolves to the Chart constructor with only the pieces chart blocks use registered.
export function loadChart() {
  if (!chartPromise) {
    chartPromise = import(/* webpackChunkName: "chat-chart" */ './chat-chart-library')
      .then(({ default: Chart }) => Chart)
      .catch(error => {
        // Let a later chart retry, e.g. after a flaky network drops the chunk.
        chartPromise = null;
        throw error;
      });
  }
  return chartPromise;
}

// The chart's data as a normalized table (see tableOf in chat-structured.js),
// for when the block carries no table of its own: one row per label, one
// column per series, missing values left empty.
export function tableFromChart({ title, labels, datasets }) {
  return {
    caption: title,
    headers: ['', ...datasets.map(dataset => dataset.label)],
    rows: labels.map((label, i) => [
      label,
      ...datasets.map(dataset => (dataset.data[i] === null ? '' : formatChartNumber(dataset.data[i]))),
    ]),
  };
}

// Chart.js config for a normalized chart (see chartOf in chat-structured.js).
export function buildChartConfig({ chartType, title, labels, datasets }, { fontFamily } = {}) {
  const isPie = chartType === 'pie';
  const font = fontFamily ? { family: fontFamily } : undefined;
  return {
    type: chartType,
    data: {
      labels,
      datasets: datasets.map((dataset, i) => {
        const color = SERIES_COLORS[i % SERIES_COLORS.length];
        return isPie
          ? { ...dataset, backgroundColor: labels.map((_label, j) => SERIES_COLORS[j % SERIES_COLORS.length]) }
          : { ...dataset, backgroundColor: color, borderColor: color, borderRadius: chartType === 'bar' ? 4 : 0, tension: 0.2 };
      }),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 400 },
      font,
      plugins: {
        title: { display: Boolean(title), text: title, font: { ...font, size: 14, weight: '600' }, color: '#202125' },
        legend: { display: isPie || datasets.length > 1, rtl: true, textDirection: 'rtl', labels: { font } },
        tooltip: {
          rtl: true,
          textDirection: 'rtl',
          callbacks: {
            label: context => {
              const value = isPie ? context.parsed : context.parsed.y;
              const name = isPie ? context.label : context.dataset.label;
              return `${name}: ${formatChartNumber(value)}`;
            },
          },
        },
      },
      scales: isPie ? {} : {
        x: { ticks: { font }, grid: { display: false } },
        y: { ticks: { font, callback: value => formatChartNumber(value) }, beginAtZero: chartType === 'bar' },
      },
    },
  };
}
